import { memo, useState } from 'react';
import { Dialog, DialogPanel, Disclosure, DisclosureButton, DisclosurePanel, PopoverGroup } from '@headlessui/react';
import {
  AcademicCapIcon,
  Bars3Icon,
  CursorArrowRaysIcon,
  GlobeAsiaAustraliaIcon,
  MagnifyingGlassIcon,
  NewspaperIcon,
  PlayCircleIcon,
  SparklesIcon,
  StarIcon,
  UserGroupIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { useLang } from 'hooks/useLang';
import { useTranslation } from 'react-i18next';
import dict from 'locales/dict';
import { ENDPOINTS } from 'utils/endpoints';
import { toTitleCase } from 'utils/common';
import ReactCountryFlag from 'react-country-flag';
import { Link, useLocation } from 'react-router-dom';
import { useThemeStore } from 'components/features';
import { XmasEffect, XmasMountain, XmasTree } from 'components/themes/xmas';
import { HoverPopover } from 'components/ui';

import vacsLogo from 'assets/images/vacs-logo.png';

const certificateIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg
    width='28'
    height='28'
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='1'
    strokeLinecap='round'
    strokeLinejoin='round'
  >
    <path stroke='none' d='M0 0h24v24H0z' fill='none' />
    <path d='M9 15a3 3 0 1 0 6 0a3 3 0 1 0 -6 0' />
    <path d='M10 7h4' />
    <path d='M10 18v4l2 -1l2 1v-4' />
    <path d='M10 19h-2a2 2 0 0 1 -2 -2v-12a2 2 0 0 1 2 -2h8a2 2 0 0 1 2 2v12a2 2 0 0 1 -2 2h-2' />
  </svg>
);

const HeaderComponent = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { lang, setLang } = useLang();
  const { t } = useTranslation();
  const location = useLocation();

  const handleClickHome = () => {
    if (location.pathname === ENDPOINTS.home) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const flagStyle1x1: any = {
    width: '1.5rem',
    height: '1.5rem',
    objectFit: 'cover',
    borderRadius: '50%',
    cursor: 'pointer',
  };

  const aboutUsItems = [
    {
      name: toTitleCase(t(dict.aboutUs)),
      description: '',
      href: ENDPOINTS.aboutUs,
      icon: StarIcon,
    },
    // {
    //   name: toTitleCase(t(dict.ourPurpose)),
    //   description: '',
    //   href: ENDPOINTS.missionVision,
    //   icon: RocketLaunchIcon,
    // },

    // {
    //   name: toTitleCase(t(dict.ourValue)),
    //   description: dict.ourValueText,
    //   href: ENDPOINTS.ourValue,
    //   icon: TrophyIcon,
    // },
    {
      name: toTitleCase(t(dict.ourTeam)),
      description: '',
      href: ENDPOINTS.ourTeam,
      icon: UserGroupIcon,
    },
    {
      name: toTitleCase(t(dict.ourServices)),
      description: 'Get a better understanding of our Storage services',
      href: ENDPOINTS.services,
      icon: SparklesIcon,
    },
  ];

  const featureItems = [
    {
      name: toTitleCase(t(dict.recruitmentActivities)),
      description: '',
      href: ENDPOINTS.recruitment,
      icon: MagnifyingGlassIcon,
    },
    {
      name: toTitleCase(t(dict.trainingPolicy)),
      description: '',
      href: ENDPOINTS.training,
      icon: AcademicCapIcon,
    },
    {
      name: toTitleCase(t(dict.sustainability)),
      description: '',
      href: ENDPOINTS.sustainability,
      icon: GlobeAsiaAustraliaIcon,
    },
    {
      name: toTitleCase(t(dict.career)),
      description: '',
      href: ENDPOINTS.career,
      icon: CursorArrowRaysIcon,
    },
  ];

  // const callsToActionItems: any[] = [
  //   {
  //     name: 'vnaircaterers.com',
  //     description: 'vnaircaterers.com',
  //     href: 'https://vnaircaterers.com',
  //     icon: HomeIcon,
  //   },
  //   {
  //     name: t(dict.getInTouch),
  //     description: dict.getInTouchText,
  //     href: ENDPOINTS.getInTouch,
  //     icon: ChatBubbleOvalLeftEllipsisIcon,
  //   },
  // ];

  const newsAndMediaItems = [
    {
      name: toTitleCase(t(dict.newsAnnouncement)),
      description: dict.newsAnnoucementText,
      href: ENDPOINTS.news,
      icon: NewspaperIcon,
    },
    {
      name: toTitleCase(t(dict.mediaGallery)),
      description: '',
      href: ENDPOINTS.mediaGalery,
      icon: PlayCircleIcon,
    },
    {
      name: toTitleCase(t(dict.achievements)),
      description: '',
      href: ENDPOINTS.achievements,
      icon: certificateIcon,
    },
    // {
    //   name: toTitleCase(t(dict.sampleMenu)),
    //   description: '',
    //   href: ENDPOINTS.sampleMenu,
    //   icon: BookOpenIcon,
    // },
    // {
    //   name: toTitleCase(t(dict.career)),
    //   description: '',
    //   href: ENDPOINTS.career,
    //   icon: CursorArrowRaysIcon,
    // },
  ];

  const { theme } = useThemeStore();

  return (
    <header className='sticky top-0 z-40 bg-brand-secondary'>
      {theme === 'xmas' && <XmasEffect snow sleign />}
      <nav
        aria-label='Global'
        className={`mx-auto flex max-w-7xl items-center justify-between p-3 lg:px-8 ${
          theme === 'xmas' ? 'relative pb-20' : ''
        }`}
      >
        {theme === 'xmas' && (
          <>
            <XmasMountain mountain1 mountain2 />
            <XmasTree />
          </>
        )}

        <div className='flex lg:flex-1 '>
          <Link to={ENDPOINTS.home} onClick={handleClickHome} className='-m-1.5 gap-3 flex items-center'>
            <img
              src={vacsLogo}
              alt='Logo'
              className='h-[4.11rem] w-auto object-cover contrast-[1.1] drop-shadow-[0_0_4px_rgba(50,50,50,0.8)]'
            />
            <span
              className={`text-xs sm:text-base md:text-2xl font-semibold bg-brand-primary-gradient bg-clip-text text-transparent ${
                theme === 'xmas' ? 'snow-shadow' : 'drop-shadow-sm drop-shadow-white'
              }`}
            >
              {'' + t(dict.longThanhAirport)}
            </span>
          </Link>
        </div>

        <div className='flex lg:hidden'>
          <button
            type='button'
            onClick={() => setMobileMenuOpen(true)}
            className='-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 text-white'
          >
            <span className='sr-only'>{t(dict.openMenu)}</span>
            <Bars3Icon aria-hidden='true' className='size-6' />
          </button>
        </div>

        <PopoverGroup className='hidden lg:flex lg:gap-x-8'>
          {/* <Popover className='relative'>
            <PopoverButton
              className={`flex items-center gap-x-1 text-lg text-white outline-none focus:outline-none focus:ring-0 ${
                theme === 'xmas' ? 'snow-shadow' : ''
              }`}
            >
              {t(dict.aboutUs)}
            </PopoverButton>
            <PopoverPanel
              transition
              className='absolute left-1/2 z-10 mt-3 w-screen max-w-md -translate-x-1/2 overflow-hidden rounded-3xl bg-white shadow-lg outline outline-1 outline-gray-900/5 transition data-[closed]:translate-y-1 data-[closed]:opacity-0 data-[enter]:duration-200 data-[leave]:duration-150 data-[enter]:ease-out data-[leave]:ease-in'
            >
              <div className='p-4'>
                {aboutUsItems.map((item) => (
                  <div
                    key={item.name}
                    className='group relative flex items-center gap-x-6 rounded-lg p-4 text-sm/6 hover:bg-brand-hover'
                  >
                    <div className='flex size-11 flex-none items-center justify-center rounded-lg group-hover:bg-white'>
                      <item.icon
                        aria-hidden='true'
                        className='size-6 text-brand-content group-hover:text-brand-primary'
                      />
                    </div>
                    <div className='flex-auto'>
                      <Link to={item.href} className='block text-lg text-brand-content group-hover:text-brand-primary'>
                        {item.name}
                        <span className='absolute inset-0' />
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
              <div className='grid grid-cols-2 divide-x divide-gray-900/5 border-t border-gray-100'>
                {callsToActionItems.map((item, index) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    target={index === 0 ? '_blank' : ''}
                    rel={index === 0 ? 'noreferrer' : ''}
                    className='group flex items-center justify-center gap-x-2.5 p-5 hover:bg-brand-hover text-lg hover:text-brand-primary'
                  >
                    <item.icon
                      aria-hidden='true'
                      className='size-5 flex-none text-brand-content group-hover:text-brand-primary'
                    />
                    {item.name}
                  </Link>
                ))}
              </div>
            </PopoverPanel>
          </Popover> */}

          <HoverPopover
            buttonName={t(dict.aboutUs)}
            buttonClassName='-mt-2 gap-x-1 py-1 px-5 rounded-2xl hover:border hover:border-brand-hover hover:bg-white/10'
          >
            <>
              <div className='p-4'>
                {aboutUsItems.map((item) => (
                  <Link
                    to={item.href}
                    key={item.name}
                    className='group relative flex items-center gap-x-6 rounded-lg p-4 text-sm/6 hover-wipe'
                  >
                    <div className='flex size-11 flex-none items-center justify-center rounded-lg group-hover:bg-white'>
                      <item.icon
                        aria-hidden='true'
                        className='size-6 text-brand-content group-hover:text-brand-primary'
                      />
                    </div>
                    <div className='flex-auto'>
                      <span className='block text-lg text-brand-content group-hover:text-brand-primary'>
                        {item.name}
                        <span className='absolute inset-0' />
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
              {/* <div className='grid grid-cols-2 divide-x divide-gray-900/5 border-t border-gray-100'>
                {callsToActionItems.map((item, index) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    target={index === 0 ? '_blank' : ''}
                    rel={index === 0 ? 'noreferrer' : ''}
                    className='group flex items-center justify-center gap-x-2.5 p-5 hover:bg-brand-hover text-lg hover:text-brand-primary'
                  >
                    <item.icon
                      aria-hidden='true'
                      className='size-5 flex-none text-brand-content group-hover:text-brand-primary'
                    />
                    {item.name}
                  </Link>
                ))}
              </div> */}
            </>
          </HoverPopover>

          <HoverPopover
            buttonName={t(dict.features)}
            buttonClassName='-mt-2 gap-x-1 py-1 px-5 rounded-2xl border-animation-secondary focus:outline-none'
          >
            <>
              <div className='p-4'>
                {featureItems.map((item) => (
                  <Link
                    to={item.href}
                    key={item.name}
                    className='group relative flex items-center gap-x-6 rounded-lg p-4 text-sm/6 hover-wipe'
                  >
                    <div className='flex size-11 flex-none items-center justify-center rounded-lg group-hover:bg-white'>
                      <item.icon
                        aria-hidden='true'
                        className='size-6 text-brand-content group-hover:text-brand-primary'
                      />
                    </div>
                    <div className='flex-auto'>
                      <span className='block text-lg text-brand-content group-hover:text-brand-primary'>
                        {item.name}
                        <span className='absolute inset-0' />
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </>
          </HoverPopover>

          {/* <Popover className='relative'>
            <PopoverButton
              className={`flex items-center -mt-2 gap-x-1 py-1 px-5 rounded-2xl text-lg border-animation-secondary focus:outline-none ${
                theme === 'xmas' ? 'button-snow button-snow-primary text-white' : ''
              }`}
            >
              {t('features')}
            </PopoverButton>
            <PopoverPanel
              transition
              className='absolute left-1/2 z-10 mt-3 w-screen max-w-md -translate-x-1/2 overflow-hidden rounded-3xl bg-white shadow-lg outline outline-1 outline-gray-900/5 transition data-[closed]:translate-y-1 data-[closed]:opacity-0 data-[enter]:duration-200 data-[leave]:duration-150 data-[enter]:ease-out data-[leave]:ease-in'
            >
              <div className='p-4'>
                {featureItems.map((item) => (
                  <div
                    key={item.name}
                    className='hover:text-brand-primary relative flex items-center gap-x-6 rounded-lg p-4 text-sm/6 hover:bg-gray-100'
                  >
                    <div className='flex size-11 flex-none items-center justify-center rounded-lg group-hover:bg-white'>
                      <item.icon aria-hidden='true' className='size-6' />
                    </div>
                    <div className='flex-auto'>
                      <Link to={item.href} className='block text-lg'>
                        {item.name}
                        <span className='absolute inset-0' />
                      </Link>
                      <p className='mt-1'>{t(item.description)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </PopoverPanel>
          </Popover> */}

          {/* <Popover className='relative'>
            <PopoverButton
              className={`flex items-center gap-x-1 text-lg text-white outline-none focus:outline-none focus:ring-0 ${
                theme === 'xmas' ? 'snow-shadow' : ''
              }`}
            >
              {t(dict.newsMedia)}
            </PopoverButton>
            <PopoverPanel
              transition
              className='absolute left-1/2 z-10 mt-3 w-screen max-w-md -translate-x-1/2 overflow-hidden rounded-3xl bg-white shadow-lg outline outline-1 outline-gray-900/5 transition data-[closed]:translate-y-1 data-[closed]:opacity-0 data-[enter]:duration-200 data-[leave]:duration-150 data-[enter]:ease-out data-[leave]:ease-in'
            >
              <div className='p-4'>
                {newsAndMediaItems.map((item) => (
                  <div
                    key={item.name}
                    className='relative flex items-center gap-x-6 rounded-lg p-4 text-sm/6 hover:bg-brand-hover'
                  >
                    <div className='flex size-11 flex-none items-center justify-center rounded-lg hover:bg-white'>
                      <item.icon aria-hidden='true' className='size-6 text-brand-content hover:text-brand-primary' />
                    </div>
                    <div className='flex-auto'>
                      <Link to={item.href} className='block text-lg text-brand-content hover:text-brand-primary'>
                        {item.name}
                        <span className='absolute inset-0' />
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            </PopoverPanel>
          </Popover> */}

          <HoverPopover
            buttonName={t(dict.newsMedia)}
            buttonClassName='-mt-2 gap-x-1 py-1 px-5 rounded-2xl hover:border hover:border-brand-hover hover:bg-white/10'
          >
            <>
              <div className='p-4 '>
                {newsAndMediaItems.map((item) => (
                  <Link
                    to={item.href}
                    key={item.name}
                    className='group relative flex items-center gap-x-6 rounded-lg p-4 text-sm/6 hover-wipe'
                  >
                    <div className='flex size-11 flex-none items-center justify-center rounded-lg group-hover:bg-white'>
                      <item.icon
                        aria-hidden='true'
                        className='size-6 text-brand-content group-hover:text-brand-primary'
                      />
                    </div>
                    <div className='flex-auto'>
                      <span className='block text-lg text-brand-content group-hover:text-brand-primary'>
                        {item.name}
                        <span className='absolute inset-0' />
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </>
          </HoverPopover>

          {/* <Link
            to={ENDPOINTS.career}
            className={`text-lg text-white ${
              theme === 'xmas'
                ? 'button-snow button-snow-blue text-sm py-2 px-4 border border-[#E9EAFA] rounded-2xl snow-shadow'
                : ''
            }`}
            title={t(dict.careerText)}
          >
            {t(dict.careerInLongThanh)}
          </Link> */}

          {lang === 'vi' && (
            <ReactCountryFlag countryCode={'VN'} svg style={flagStyle1x1} onClick={() => setLang('en')} />
          )}
          {lang === 'en' && (
            <ReactCountryFlag countryCode={'US'} svg style={flagStyle1x1} onClick={() => setLang('vi')} />
          )}

          {/* <ThemeToogleButton /> */}
        </PopoverGroup>
      </nav>

      {/* Menu Mobile */}
      <Dialog open={mobileMenuOpen} onClose={setMobileMenuOpen} className='lg:hidden'>
        <div className='inset-0 z-50' />
        <DialogPanel className='fixed inset-y-0 right-0 z-50 w-full overflow-y-auto bg-brand-secondary p-6 sm:max-w-sm sm:ring-1 sm:ring-gray-900/10'>
          <button type='button' onClick={() => setMobileMenuOpen(false)} className='-m-2.5 rounded-md p-2.5 text-white'>
            <span className='sr-only'>{t(dict.close)}</span>
            <XMarkIcon aria-hidden='true' className='size-6' />
          </button>
          <div className='mt-6 flex items-center justify-between'>
            <Link to={ENDPOINTS.home} className='-m-1.5 p-1.5 drop-shadow-sm drop-shadow-white rounded-full'>
              <span className='sr-only'>VACS Long Thanh</span>
              <img alt='' src={vacsLogo} className='h-18 w-auto' />
            </Link>
            {/* <button
              type='button'
              onClick={() => setMobileMenuOpen(false)}
              className='-m-2.5 rounded-md p-2.5 text-white'
            >
              <span className='sr-only'>{t(dict.close)}</span>
              <XMarkIcon aria-hidden='true' className='size-6' />
            </button> */}
          </div>
          <div className='mt-6 flow-root'>
            <div className='-my-6 divide-y divide-gray-500/10'>
              <div className='space-y-2 py-6'>
                <Disclosure as='div' className='-mx-3'>
                  <DisclosureButton className='group flex w-full items-center justify-between rounded-lg py-2 pl-3 pr-3.5 text-base/7 text-white hover:bg-brand-blue'>
                    {t(dict.aboutUs)}
                    <ChevronDownIcon aria-hidden='true' className='size-5 flex-none group-data-[open]:rotate-180' />
                  </DisclosureButton>
                  <DisclosurePanel className='mt-2 space-y-2'>
                    {aboutUsItems.map((item) => (
                      <DisclosureButton
                        key={item.name}
                        as='a'
                        href={item.href}
                        className='block rounded-lg py-2 pl-6 pr-3 text-sm/7 text-white hover:bg-brand-blue font-semibold'
                      >
                        {item.name}
                      </DisclosureButton>
                    ))}
                  </DisclosurePanel>
                </Disclosure>
                <Disclosure as='div' className='-mx-3'>
                  <DisclosureButton className='group flex w-full items-center justify-between rounded-lg py-2 pl-3 pr-3.5 text-base/7 text-white hover:bg-brand-blue'>
                    {t(dict.features)}
                    <ChevronDownIcon aria-hidden='true' className='size-5 flex-none group-data-[open]:rotate-180' />
                  </DisclosureButton>
                  <DisclosurePanel className='mt-2 space-y-2'>
                    {featureItems.map((item) => (
                      <DisclosureButton
                        key={item.name}
                        as='a'
                        href={item.href}
                        className='block rounded-lg py-2 pl-6 pr-3 text-sm/7 text-white hover:bg-brand-blue font-semibold'
                      >
                        {item.name}
                      </DisclosureButton>
                    ))}
                  </DisclosurePanel>
                </Disclosure>
                <Disclosure as='div' className='-mx-3'>
                  <DisclosureButton className='group flex w-full items-center justify-between rounded-lg py-2 pl-3 pr-3.5 text-base/7 text-white hover:bg-brand-blue'>
                    {t(dict.newsMedia)}
                    <ChevronDownIcon aria-hidden='true' className='size-5 flex-none group-data-[open]:rotate-180' />
                  </DisclosureButton>
                  <DisclosurePanel className='mt-2 space-y-2'>
                    {newsAndMediaItems.map((item) => (
                      <DisclosureButton
                        key={item.name}
                        as='a'
                        href={item.href}
                        className='block rounded-lg py-2 pl-6 pr-3 text-sm/7 text-white hover:bg-brand-blue font-semibold'
                      >
                        {item.name}
                      </DisclosureButton>
                    ))}
                  </DisclosurePanel>
                </Disclosure>
                {/* <Link
                  to={ENDPOINTS.career}
                  className='-mx-3 block rounded-lg px-3 py-2 text-base/7 text-white hover:bg-brand-blue'
                >
                  {t(dict.careerInLongThanh)}
                </Link> */}
                {lang === 'vi' && (
                  <ReactCountryFlag countryCode={'VN'} svg style={flagStyle1x1} onClick={() => setLang('en')} />
                )}
                {lang === 'en' && (
                  <ReactCountryFlag countryCode={'US'} svg style={flagStyle1x1} onClick={() => setLang('vi')} />
                )}
                {/* <p className='mt-6'>
                  <ThemeToogleButton />
                </p> */}
              </div>
            </div>
          </div>
        </DialogPanel>
      </Dialog>
    </header>
  );
};

export const Header = memo(HeaderComponent);
